import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { readAuthToken } from '@noor/api';
import { env } from '@noor/config';
import {
  buildCouriersListFilter,
  courierAdminQueries,
  courierListSearchSchema,
  parseCourierSort,
  type CourierListSearch,
} from '@/entities/courier-admin';
import { CouriersPage } from '@/pages/couriers';

/**
 * Couriers route — the URL is the single source of truth for the status tab,
 * filters, sort and page. Prefetch is best-effort and non-blocking; the table
 * widget surfaces its own loading/error state.
 */
export const Route = createFileRoute('/couriers/')({
  staticData: { permission: 'couriers.view' },
  beforeLoad: () => {
    // Auth guard: against the real API a token is required. In MSW dev mode the
    // mock backend authenticates everything, so the guard only bites with MSW off.
    if (!env.VITE_ENABLE_MSW && !readAuthToken()) {
      // `redirect()` is TanStack Router's control-flow throw, not an Error.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: courierListSearchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient
      .prefetchQuery(
        courierAdminQueries.list(
          buildCouriersListFilter({ values: deps, sort: parseCourierSort(deps.sort) }),
        ),
      )
      .catch(() => undefined);
  },
  component: CouriersRoute,
});

function CouriersRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const onParamsChange = (next: Partial<CourierListSearch>) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        ...next,
      }),
    });
  };

  return (
    <CouriersPage
      params={search}
      onParamsChange={onParamsChange}
      onOpenCourier={(courierId) =>
        void navigate({ to: '/couriers/$courierId', params: { courierId } })
      }
      onOpenChat={(chat) => void navigate({ to: '/inbox', search: { chat } })}
    />
  );
}
